import type { DrilldownRow } from "@gen/tracker/v1/analytics_pb";
import { DimensionFilterOp, MetricFilterOp, type StatsRow } from "@gen/tracker/v1/reporting_pb";

import { aggregate, type Bag } from "@/lib/campaigns-table";
import { statsRowToNumbers, type ReportingSchema } from "@/lib/services/reporting-schema-service";

/**
 * The report's state and shapes (доски Report, ReportDims, ReportTime). The
 * numbers are the server's: every row — a campaign, its ads, their countries —
 * is counted by GetBreakdownDrilldown (with_subtotals), so a parent's uniques
 * are people and never a sum of its children's. Nothing here re-derives a
 * report row; the only sums are over hour buckets on «По гео и часам», and
 * those go through the registry's formulas (`aggregate`). Pure.
 */

export const MAX_LEVELS = 4;

// --- filters in the URL ------------------------------------------------------

export interface DimFilter {
  key: string;
  op: DimensionFilterOp;
  values: string[];
}
export interface MetricCond {
  key: string;
  op: MetricFilterOp;
  /** In the metric's own unit: a percent is a ratio (0.05 = 5 %). */
  value: string;
}
export interface ReportFilters {
  dims: DimFilter[];
  metrics: MetricCond[];
}

const DIM_OPS: Record<string, DimensionFilterOp> = {
  in: DimensionFilterOp.IN,
  nin: DimensionFilterOp.NOT_IN,
  has: DimensionFilterOp.CONTAINS,
  nhas: DimensionFilterOp.NOT_CONTAINS,
};
const METRIC_OPS: Record<string, MetricFilterOp> = {
  gt: MetricFilterOp.GT,
  gte: MetricFilterOp.GTE,
  lt: MetricFilterOp.LT,
  lte: MetricFilterOp.LTE,
  eq: MetricFilterOp.EQ,
  neq: MetricFilterOp.NEQ,
};
const word = <T,>(m: Record<string, T>, v: T) => Object.keys(m).find((k) => m[k] === v) ?? "";

/**
 * `flt` param: `d:source:in:Facebook,TikTok;m:clicks:gt:50`. Values are
 * URI-encoded, so a comma or colon in a value survives. Pure.
 */
export function parseFilters(raw: string | null | undefined): ReportFilters {
  const out: ReportFilters = { dims: [], metrics: [] };
  for (const part of (raw ?? "").split(";")) {
    const [kind, key, op, rest] = part.split(":");
    if (!kind || !key || !op || rest === undefined) continue;
    if (kind === "d" && DIM_OPS[op] !== undefined) {
      const values = rest.split(",").map(decodeURIComponent).filter((v) => v !== "");
      if (values.length) out.dims.push({ key, op: DIM_OPS[op]!, values });
    } else if (kind === "m" && METRIC_OPS[op] !== undefined && rest !== "") {
      out.metrics.push({ key, op: METRIC_OPS[op]!, value: decodeURIComponent(rest) });
    }
  }
  return out;
}

export function formatFilters(f: ReportFilters): string {
  return [
    ...f.dims.map((d) => `d:${d.key}:${word(DIM_OPS, d.op)}:${d.values.map(encodeURIComponent).join(",")}`),
    ...f.metrics.map((m) => `m:${m.key}:${word(METRIC_OPS, m.op)}:${encodeURIComponent(m.value)}`),
  ].join(";");
}

// --- the tree ------------------------------------------------------------------

export interface ReportNode {
  id: string;
  path: string[];
  depth: number;
  v: Bag;
  prev?: Bag;
  children: ReportNode[];
}

const ID_SEP = "\u0001";

/**
 * Rows arrive in tree order with every level counted (`with_subtotals`): a
 * parent, then its children. Nest them by path. A row whose parent is not in
 * the page (paging is over the top level, so this does not happen) becomes a
 * root rather than vanishing. Pure.
 */
export function buildTree(rows: DrilldownRow[]): ReportNode[] {
  const roots: ReportNode[] = [];
  const byId = new Map<string, ReportNode>();
  for (const r of rows) {
    const path = r.dimensionPath;
    if (path.length === 0) continue;
    // Spend no click carries has no ad, lander or country under it: one line,
    // «Расход без кликов», not a chain of the same line at every level.
    if (r.spendWithoutClicks && path.length > 1) continue;
    const node: ReportNode = {
      id: path.join(ID_SEP),
      path,
      depth: path.length - 1,
      v: statsRowToNumbers(r.stats),
      prev: r.compare ? statsRowToNumbers(r.compare) : undefined,
      children: [],
    };
    byId.set(node.id, node);
    const parent = path.length > 1 ? byId.get(path.slice(0, -1).join(ID_SEP)) : undefined;
    (parent ? parent.children : roots).push(node);
  }
  return roots;
}

/** Rows of every level, in the order a CSV and a screen reader meet them. Pure. */
export function flatten(nodes: ReportNode[]): ReportNode[] {
  return nodes.flatMap((n) => [n, ...flatten(n.children)]);
}

export const toBag = (s: StatsRow | undefined): Bag | undefined => (s ? statsRowToNumbers(s) : undefined);

// --- day of week × hour --------------------------------------------------------

export interface TimeGrid {
  /** cell[weekday 0..6 (Mon…Sun)][hour 0..23] */
  cell: (Bag | undefined)[][];
  /** Σ over the week per hour (the server's hour rows). */
  hour: (Bag | undefined)[];
  /** Σ over the day per weekday (the server's weekday rows). */
  weekday: (Bag | undefined)[];
}

/**
 * Weekday rows ("1" = Monday) with their hour children, and the hour-only
 * rows. Pure.
 */
export function buildTimeGrid(grid: DrilldownRow[], hours: DrilldownRow[]): TimeGrid {
  const out: TimeGrid = {
    cell: Array.from({ length: 7 }, () => Array<Bag | undefined>(24).fill(undefined)),
    hour: Array<Bag | undefined>(24).fill(undefined),
    weekday: Array<Bag | undefined>(7).fill(undefined),
  };
  for (const r of grid) {
    const d = Number(r.dimensionPath[0]) - 1;
    if (!(d >= 0 && d < 7)) continue;
    if (r.dimensionPath.length === 1) out.weekday[d] = statsRowToNumbers(r.stats);
    else {
      const h = Number(r.dimensionPath[1]);
      if (h >= 0 && h < 24) out.cell[d]![h] = statsRowToNumbers(r.stats);
    }
  }
  for (const r of hours) {
    const h = Number(r.dimensionPath[0]);
    if (h >= 0 && h < 24) out.hour[h] = statsRowToNumbers(r.stats);
  }
  return out;
}

export interface HourWindow {
  from: number;
  /** Inclusive. */
  to: number;
  v: Bag;
}

/** Hours [from, to] summed through the registry. Pure. */
export function windowBag(hours: (Bag | undefined)[], from: number, to: number, schema: ReportingSchema): Bag {
  const bags: Bag[] = [];
  for (let h = from; h <= to; h++) if (hours[h]) bags.push(hours[h]!);
  return aggregate(bags, schema);
}

/**
 * The best and the worst run of `len` hours by profit, among runs that spent
 * something. Runs do not wrap past midnight. Pure.
 */
export function extremeWindows(hours: (Bag | undefined)[], schema: ReportingSchema, len = 4): { best?: HourWindow; worst?: HourWindow } {
  let best: HourWindow | undefined;
  let worst: HourWindow | undefined;
  for (let from = 0; from + len - 1 < 24; from++) {
    const v = windowBag(hours, from, from + len - 1, schema);
    if (!((v.cost ?? 0) > 0)) continue;
    const w = { from, to: from + len - 1, v };
    if (!best || (v.profit ?? 0) > (best.v.profit ?? 0)) best = w;
    if (!worst || (v.profit ?? 0) < (worst.v.profit ?? 0)) worst = w;
  }
  return { best, worst };
}

/** Saturday and Sunday against Monday–Friday, through the registry. Pure. */
export function weekendVsWeekdays(weekday: (Bag | undefined)[], schema: ReportingSchema): { weekend: Bag; weekdays: Bag } {
  const pick = (idx: number[]) => aggregate(idx.flatMap((i) => (weekday[i] ? [weekday[i]!] : [])), schema);
  return { weekend: pick([5, 6]), weekdays: pick([0, 1, 2, 3, 4]) };
}

/** A run of hours, wrapping past midnight: `from` 22, `len` 5 is 22:00–03:00. */
export interface HourRun {
  from: number;
  len: number;
  sum: number;
}

/**
 * The run of hours that lost the most — the most negative circular run of
 * `values` (a profit per hour). Undefined when no hour lost. Pure.
 */
export function worstRun(values: readonly (number | undefined)[]): HourRun | undefined {
  const n = values.length;
  let best: HourRun | undefined;
  for (let from = 0; from < n; from++) {
    let sum = 0;
    for (let len = 1; len < n; len++) {
      sum += values[(from + len - 1) % n] ?? 0;
      if (sum < 0 && (!best || sum < best.sum)) best = { from, len, sum };
    }
  }
  return best;
}

/**
 * The hours to keep showing an ad: the day minus its worst run, as
 * «07:00 – 01:00» (the end is the start of the cut). Null = the whole day. Pure.
 */
export function keepWindow(run: HourRun | undefined): { from: number; to: number } | null {
  if (!run) return null;
  return { from: (run.from + run.len) % 24, to: run.from };
}

export const hh = (h: number) => `${String(h).padStart(2, "0")}:00`;
